import React, { useEffect, useState } from 'react'
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Flex,
  useColorModeValue,
  useColorMode,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Progress,
} from '@chakra-ui/react'
import { motion, useAnimation } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import LEColorConstants, {
  basicTextColor,
} from '@/lib/constants/colorConstants'
import CustomButton from '../Button'
import router from 'next/router'

export const UrgencySection = () => {
  const { colorMode } = useColorMode()
  const bgColor = useColorModeValue('gray.50', 'gray.900')
  const cardBg = useColorModeValue('white', 'gray.800')

  const controls = useAnimation()
  const { inView, ref } = useInView({ threshold: 0.3 })

  const [timeLeft, setTimeLeft] = useState({
    months: 16,
    days: 15,
    hours: 8,
  })

  useEffect(() => {
    if (inView) {
      controls.start('visible')
    }
  }, [controls, inView])

  // Simulate countdown (in real scenario, this would be to actual CBT implementation date)
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev.hours > 0) {
          return { ...prev, hours: prev.hours - 1 }
        } else if (prev.days > 0) {
          return { ...prev, days: prev.days - 1, hours: 23 }
        } else if (prev.months > 0) {
          return { ...prev, months: prev.months - 1, days: 29, hours: 23 }
        }
        return prev
      })
    }, 3600000) // Update every hour

    return () => clearInterval(timer)
  }, [])

  const goToSignUp = () => {
    const host =
      process.env.NEXT_PUBLIC_VERCEL_ENV === 'production'
        ? process.env.NEXT_PUBLIC_APP_HOST
        : process.env.NEXT_PUBLIC_APP_LOCALHOST

    return router.push(host + '/signup')
  }

  return (
    <Box
      bg={bgColor}
      py="5rem"
      ref={ref}
      as={motion.div}
      animate={controls}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
    >
      <Container maxW="90rem" mx="auto" px={['1rem', '3rem', '4rem', '6rem']}>
        <VStack spacing="3rem" textAlign="center">
          {/* Main Headline */}
          <Box
            as={motion.div}
            variants={{
              hidden: { opacity: 0, y: 50 },
              visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
            }}
          >
            <Heading
              fontSize={['2rem', '2.5rem', '3rem']}
              fontWeight="bold"
              color={basicTextColor[colorMode]}
              mb="1rem"
            >
              The CBT Revolution is{' '}
              <Text as="span" color={LEColorConstants.secondary}>
                Already Here
              </Text>
            </Heading>

            <Text
              fontSize={['1.1rem', '1.3rem']}
              color={basicTextColor[colorMode]}
              maxW="4xl"
              mx="auto"
              fontWeight="medium"
            >
              While other schools wait and hope, forward-thinking principals are
              already preparing their students for the inevitable digital
              transformation.
            </Text>
          </Box>

          {/* Countdown Timer */}
          <Box
            bg="linear-gradient(135deg, #009BFF 0%, #FF6E06 100%)"
            borderRadius="16px"
            p="2rem"
            w="full"
            maxW="2xl"
            as={motion.div}
            variants={{
              hidden: { opacity: 0, scale: 0.9 },
              visible: {
                opacity: 1,
                scale: 1,
                transition: { duration: 0.6, delay: 0.2 },
              },
            }}
          >
            <Text color="white" fontSize="1.2rem" fontWeight="bold" mb="1rem">
              ⏰ Time Until Full CBT Implementation
            </Text>

            <HStack justify="center" spacing="2rem">
              <VStack spacing="0">
                <Text color="white" fontSize="2.5rem" fontWeight="bold">
                  {timeLeft.months}
                </Text>
                <Text color="white" fontSize="0.9rem">
                  MONTHS
                </Text>
              </VStack>

              <VStack spacing="0">
                <Text color="white" fontSize="2.5rem" fontWeight="bold">
                  {timeLeft.days}
                </Text>
                <Text color="white" fontSize="0.9rem">
                  DAYS
                </Text>
              </VStack>

              <VStack spacing="0">
                <Text color="white" fontSize="2.5rem" fontWeight="bold">
                  {timeLeft.hours}
                </Text>
                <Text color="white" fontSize="0.9rem">
                  HOURS
                </Text>
              </VStack>
            </HStack>
          </Box>

          {/* Current Statistics */}
          <SimpleGrid
            columns={{ base: 1, md: 3 }}
            spacing="2rem"
            w="full"
            as={motion.div}
            variants={{
              hidden: { opacity: 0, y: 30 },
              visible: {
                opacity: 1,
                y: 0,
                transition: { duration: 0.6, delay: 0.4 },
              },
            }}
          >
            <Box
              bg={cardBg}
              p="2rem"
              borderRadius="12px"
              textAlign="center"
              boxShadow="lg"
            >
              <Stat>
                <StatNumber color={LEColorConstants.primary} fontSize="3rem">
                  100%
                </StatNumber>
                <StatLabel fontSize="1.1rem" fontWeight="bold">
                  JAMB Already CBT
                </StatLabel>
                <StatHelpText>Over 1.8 million candidates yearly</StatHelpText>
              </Stat>
            </Box>

            <Box
              bg={cardBg}
              p="2rem"
              borderRadius="12px"
              textAlign="center"
              boxShadow="lg"
            >
              <Stat>
                <StatNumber color={LEColorConstants.secondary} fontSize="3rem">
                  2026
                </StatNumber>
                <StatLabel fontSize="1.1rem" fontWeight="bold">
                  WAEC & NECO CBT Launch
                </StatLabel>
                <StatHelpText>Confirmed transition date</StatHelpText>
              </Stat>
            </Box>

            <Box
              bg={cardBg}
              p="2rem"
              borderRadius="12px"
              textAlign="center"
              boxShadow="lg"
            >
              <Stat>
                <StatNumber color={LEColorConstants.colorCheck} fontSize="3rem">
                  73%
                </StatNumber>
                <StatLabel fontSize="1.1rem" fontWeight="bold">
                  Schools Unprepared
                </StatLabel>
                <StatHelpText>Don't be part of this statistic</StatHelpText>
              </Stat>
            </Box>
          </SimpleGrid>

          {/* The Consequence */}
          <Box
            bg="linear-gradient(135deg, rgba(229, 62, 62, 0.1) 0%, rgba(245, 101, 101, 0.1) 100%)"
            border="2px solid"
            borderColor="red.300"
            borderRadius="16px"
            p="2.5rem"
            maxW="4xl"
            w="full"
            as={motion.div}
            variants={{
              hidden: { opacity: 0, x: -50 },
              visible: {
                opacity: 1,
                x: 0,
                transition: { duration: 0.6, delay: 0.6 },
              },
            }}
          >
            <Heading fontSize={['1.5rem', '2rem']} color="red.600" mb="1rem">
              ⚠️ The Cost of Waiting
            </Heading>

            <VStack spacing="1rem" align="start">
              <HStack>
                <Text fontSize="1.2rem" fontWeight="bold" color="red.500">
                  ❌
                </Text>
                <Text fontSize={['1rem', '1.1rem']}>
                  Students struggle with unfamiliar CBT interface during actual
                  exams
                </Text>
              </HStack>

              <HStack>
                <Text fontSize="1.2rem" fontWeight="bold" color="red.500">
                  ❌
                </Text>
                <Text fontSize={['1rem', '1.1rem']}>
                  Lower exam scores due to test anxiety and unfamiliarity
                </Text>
              </HStack>

              <HStack>
                <Text fontSize="1.2rem" fontWeight="bold" color="red.500">
                  ❌
                </Text>
                <Text fontSize={['1rem', '1.1rem']}>
                  School reputation suffers as competitors get ahead
                </Text>
              </HStack>

              <HStack>
                <Text fontSize="1.2rem" fontWeight="bold" color="red.500">
                  ❌
                </Text>
                <Text fontSize={['1rem', '1.1rem']}>
                  Parents lose confidence and consider transferring students
                </Text>
              </HStack>
            </VStack>
          </Box>

          {/* Progress Bar */}
          <Box w="full" maxW="3xl">
            <Flex justify="space-between" mb="0.5rem">
              <Text fontSize="0.9rem" fontWeight="bold">
                CBT Preparation Progress
              </Text>
              <Text
                fontSize="0.9rem"
                fontWeight="bold"
                color={LEColorConstants.primary}
              >
                27% of schools prepared
              </Text>
            </Flex>
            <Progress
              value={27}
              colorScheme="blue"
              size="lg"
              borderRadius="full"
            />
            <Text
              fontSize="0.8rem"
              color="gray.600"
              mt="0.5rem"
              textAlign="center"
            >
              Don't be left in the 73% - Join the prepared schools today!
            </Text>
          </Box>

          {/* Final CTA */}
          <Box textAlign="center">
            <CustomButton
              size="lg"
              w={{ base: 'full', sm: 'auto' }}
              borderRadius="12px"
              fontWeight="bold"
              fontSize={['0.9rem', '1.2rem']}
              onClick={goToSignUp}
              bg="linear-gradient(135deg, #009BFF 0%, #FF6E06 100%)"
              color="white"
              _hover={{
                transform: 'translateY(-3px)',
                boxShadow: '2xl',
              }}
              _active={{
                transform: 'translateY(0)',
              }}
              transition="all 0.2s"
            >
              Start Preparing Now - Get 500 Free Credits
            </CustomButton>

            <Text fontSize="0.9rem" color="gray.600" mt="1rem">
              ✅ No setup fees • ✅ Instant access • ✅ 24/7 support
            </Text>
          </Box>
        </VStack>
      </Container>
    </Box>
  )
}
